'use client';

import React, { useEffect, useRef, useState } from 'react';
import type { QuestionConfig, AnswerData } from '../types';
import styles from './ConsentQuestion.module.css';
import "./consent-question.css"
import { useTranslation } from 'react-i18next';
import { useTranslation as useTranslationClient } from '@/app/i18n/client';
import * as Tooltip from '@radix-ui/react-tooltip';

type CheckboxInformationOption = 'POPUP' | 'UNDERCHECKBOX';

interface ConsentCheckbox {
  id: string;
  label: string;
  labelEn?: string;
  isRequired: boolean;
  informationOption?: CheckboxInformationOption;
  informationText?: string;
  informationTextEn?: string;
}

interface ConsentQuestionConfig extends QuestionConfig {
  consentText?: string;
  consentTextEn?: string;
  checkboxes?: ConsentCheckbox[];
}

interface ConsentQuestionProps {
  config: ConsentQuestionConfig;
  value: AnswerData;
  onChange: (value: AnswerData) => void;
  error?: string;
  onEnterPress?: () => void; // Callback when Enter is pressed after consent
}

// Info Icon with Tooltip Component
//
// Renders via Tooltip.Portal (mounts at document.body) rather than as a plain
// absolutely-positioned child. The checkbox this sits on lives inside the
// consent box's own overflow-auto scroll region, nested inside the question
// wrapper's overflow-hidden — both clip a same-tree absolute div regardless of
// z-index, which was cutting the popup off before it could show its full text.
// A portal escapes both clipping ancestors entirely.
function InfoTooltip({ text }: { text: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Tooltip.Provider>
      <Tooltip.Root open={isOpen} onOpenChange={setIsOpen} delayDuration={200}>
        <Tooltip.Trigger asChild>
          <span
            className="relative inline-flex items-center justify-center w-4 h-4 ml-1.5 text-xs font-bold text-black-500 border border-black rounded-full hover:bg-black-50 transition-colors cursor-help"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsOpen((prev) => !prev);
            }}
          >
            i
          </span>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            className="z-50 w-64 sm:w-80 bg-red-50 border border-black text-black-700 text-xs p-3 rounded-lg shadow-lg whitespace-pre-wrap"
            sideOffset={8}
            collisionPadding={8}
          >
            {text}
            <Tooltip.Arrow className="fill-red-50" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}

export default function ConsentQuestion({
  config,
  value,
  onChange,
  error,
  onEnterPress,
}: ConsentQuestionProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const checkboxSectionRef = useRef<HTMLDivElement>(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [checkedBoxes, setCheckedBoxes] = useState<Set<string>>(new Set());

  const checkboxes = config.checkboxes || [];
  const { t, i18n } = useTranslation();
  const { lang } = useTranslationClient();

  // Helper function to get text in correct language
  const getText = (th: string | undefined, en: string | undefined): string => {
    if (lang === 'en') {
      return en || th || '';
    }
    return th || en || '';
  };

  // Parse existing values from parent component
  useEffect(() => {
    if (value?.values && Array.isArray(value.values)) {
      // Filter out metadata markers
      const actualCheckboxIds = value.values.filter(v => !v.startsWith('__scrolled__'));
      setCheckedBoxes(new Set(actualCheckboxIds));
      
      // Check if scroll marker exists
      if (value.values.includes('__scrolled__')) {
        setHasScrolledToBottom(true);
      }
    }
  }, [value]);

  // Check if checkbox section is visible (using IntersectionObserver)
  useEffect(() => {
    if (hasScrolledToBottom) return; // Already enabled, no need to observe

    const container = scrollContainerRef.current;
    const checkboxSection = checkboxSectionRef.current;
    
    if (!container || !checkboxSection) {
      // No checkboxes or refs not ready - check if content fits without scroll
      if (container && container.scrollHeight <= container.clientHeight) {
        setHasScrolledToBottom(true);
        const newValues = [...Array.from(checkedBoxes), '__scrolled__'];
        onChange({ values: newValues });
      }
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Checkbox section is now visible
            setHasScrolledToBottom(true);
            const newValues = [...Array.from(checkedBoxes), '__scrolled__'];
            onChange({ values: newValues });
            observer.disconnect();
          }
        });
      },
      {
        root: container,
        threshold: 0.1, // 10% of checkbox section visible
      }
    );

    observer.observe(checkboxSection);

    return () => observer.disconnect();
  }, [hasScrolledToBottom, checkboxes.length, checkedBoxes, onChange]);

  // Check if scrolled to bottom
  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const threshold = 10; // 10px threshold for "close enough" to bottom
    const isAtBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight <= threshold;

    if (isAtBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
      // Update parent with scroll marker
      const newValues = [...Array.from(checkedBoxes), '__scrolled__'];
      onChange({ values: newValues });
    }
  };

  // Handle checkbox toggle
  const handleCheckboxChange = (checkboxId: string) => {
    const newCheckedBoxes = new Set(checkedBoxes);
    
    if (newCheckedBoxes.has(checkboxId)) {
      newCheckedBoxes.delete(checkboxId);
    } else {
      newCheckedBoxes.add(checkboxId);
    }

    setCheckedBoxes(newCheckedBoxes);
    
    // Include scroll marker if already scrolled
    const newValues = hasScrolledToBottom 
      ? [...Array.from(newCheckedBoxes), '__scrolled__']
      : Array.from(newCheckedBoxes);
    
    onChange({ values: newValues });
  };

  const consentHtml = config.consentText || config.consentTextEn || '';

  // Check if all required checkboxes are checked
  const allRequiredChecked = checkboxes
    .filter((cb) => cb.isRequired)
    .every((cb) => {
      return checkedBoxes.has(cb.id);
    });

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Trigger next when Enter is pressed, user has scrolled to bottom, and all required checkboxes are checked
    const canProceed = checkboxes.length === 0 || (hasScrolledToBottom && allRequiredChecked);
    if (e.key === 'Enter' && canProceed && onEnterPress) {
      e.preventDefault();
      onEnterPress();
    }
  };

  return (
    <div
        className="consent-question flex flex-col items-center gap-5 md:w-2/3 w-4/5"
        onKeyDown={handleKeyDown}
        tabIndex={0}
    >
      {/* Scrollable Consent Box with checkboxes inside */}
      <div
        id="consent-scroll-container"
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className={`w-full border-2 border-[#DDD] rounded-2xl bg-white shadow-sm overflow-auto ${styles.consentScrollContainer}`}
        style={{
          padding: '1.5rem',
        }}
      >
        {/* Render HTML content - strip out inline styles from the HTML */}
        <div
            id="consent-text-content"
          className="prose prose-sm max-w-none mb-6"
          dangerouslySetInnerHTML={{ 
            __html: consentHtml
              .replace(/<style[^>]*>[\s\S]*?<\/style>/g, '') // Remove style tags
              .replace(/style="[^"]*"/g, '') // Remove inline styles
          }}
          style={{
            fontFamily: 'inherit',
            fontSize: '0.875rem',
            lineHeight: '1.6',
            color: '#333',
          }}
        />

        {/* Checkboxes inside scroll container - Classic style */}
        {checkboxes.length > 0 && (
          <div ref={checkboxSectionRef} className="flex flex-col gap-3 pt-4 border-t-2 border-gray-200">
            {checkboxes.map((checkbox) => {
              const label = getText(checkbox.label, checkbox.labelEn);
              const infoText = getText(checkbox.informationText, checkbox.informationTextEn);
              const infoOption = checkbox.informationOption || 'POPUP';
              const inputId = `consent-checkbox-${checkbox.id}`;
              
              // Check if info text should be displayed (not __NONE__ keyword)
              const shouldShowInfo = infoText && infoText !== '__NONE__';
              
              return (
                <div key={checkbox.id} className="flex flex-col gap-1">
                  <label
                    htmlFor={inputId}
                    className={`flex items-start gap-3 p-3 rounded-lg border transition-all duration-200 cursor-pointer ${
                      !hasScrolledToBottom 
                        ? 'opacity-40 cursor-not-allowed bg-gray-50 border-gray-200' 
                        : 'bg-white border-gray-300 hover:border-[#028D7B] hover:bg-gray-50'
                    }`}
                  >
                    <input
                      id={inputId}
                      type="checkbox"
                      checked={checkedBoxes.has(checkbox.id)}
                      onChange={() => handleCheckboxChange(checkbox.id)}
                      disabled={!hasScrolledToBottom}
                      className="mt-0.5 w-5 h-5 text-[#028D7B] border-gray-300 rounded focus:ring-[#028D7B] focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                    />
                    <span className="flex-1 text-sm text-gray-700 leading-relaxed">
                      {label}
                      {checkbox.isRequired && <span className="text-red-500 ml-1 font-semibold">*</span>}
                      {/* POPUP: Show info icon with tooltip (skip if __NONE__) */}
                      {infoOption === 'POPUP' && shouldShowInfo && (
                        <InfoTooltip text={infoText} />
                      )}
                    </span>
                  </label>
                  {/* UNDERCHECKBOX: Show text below checkbox (skip if __NONE__) */}
                  {infoOption === 'UNDERCHECKBOX' && shouldShowInfo && (
                    <div className="ml-8 text-xs text-red-600 leading-relaxed whitespace-pre-wrap px-3 py-1">
                      {infoText}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

        <div className="w-full flex flex-col items-center gap-3">
          {/* Scroll indicator (only when there are checkboxes) */}
          {checkboxes.length > 0 && (
            !hasScrolledToBottom ? (
              <div className="text-sm text-[#F59E0B] font-medium flex items-center gap-2">
              <svg
                className="w-5 h-5 animate-bounce"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
                />
              </svg>
              <span className="hidden sm:inline">{getText('กรุณาเลื่อนอ่านข้อความจนถึงท้ายสุด', 'Please scroll to read the message until the end')}</span>
              <span className="sm:hidden">{getText('กรุณาอ่านจนจบ', 'Please read to the end')}</span>
              </div>
            ) : (
              <div className="text-sm text-[#10B981] font-medium flex items-center gap-2">
              <svg
                className="w-5 h-5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
                />
              </svg>
              <span className="hidden sm:inline">{getText('อ่านข้อความครบถ้วนแล้ว', 'Message read completely')}</span>
              <span className="sm:hidden">{getText('อ่านครบแล้ว', 'Read complete')}</span>
              </div>
            )
          )}

          {/* Validation message */}
          {checkboxes.length > 0 && hasScrolledToBottom && !allRequiredChecked && checkboxes.some(cb => cb.isRequired) && (
            <div className="text-sm text-red-600 font-medium">
            {getText('กรุณายอมรับเงื่อนไขที่จำเป็นทั้งหมด', 'Please accept all required terms')}
            </div>
          )}

          {error && (
            <p className="text-red-600 text-sm text-center">{error}</p>
          )}
        </div>
    </div>
  );
}
